import { useEffect, useRef, useState } from "react";
import { Icon } from "../../kit";

export interface LibItem {
  id: string;
  label: string;
  /** Thumbnail URL (image) or an inline CSS background (gradient swatch). */
  thumb?: string;
  swatch?: string;
}

/** Sentinel keys for the two non-item cards, so they share the pending slot. */
const UPLOAD_KEY = " upload";
const RESET_KEY = " reset";

const errText = (e: unknown) => (e instanceof Error && e.message ? e.message : "Failed to load.");

/**
 * Reusable asset library grid — thumbnail cards with an active ring, plus
 * optional "Add custom" (upload) and "Reset / None" cards. Mirrors iJewel's
 * environment / background / material library grids. Card vs list density is
 * controlled by the host (the editor's `libraryView`).
 *
 * Pending state: the assets behind these tiles are 1–2 MB HDRs that the viewer
 * must decode and PMREM-prefilter, which takes seconds. If `onSelect` (or
 * `onUpload` / `onReset`) returns a promise the grid holds a spinner on that
 * card and locks its siblings until it settles, so a user can no longer queue
 * five overlapping env loads by clicking around, and a rejection is shown
 * inline instead of vanishing as an unhandled rejection. Hosts that own the
 * in-flight flag themselves can drive the same affordance with `busyId` /
 * `disabled`.
 *
 * The host is expected to move `activeId` only once its load RESOLVES —
 * otherwise the check-mark claims an asset is applied while its decode is
 * still running.
 */
export function LibraryGrid({
  items,
  activeId,
  onSelect,
  onUpload,
  onReset,
  view = "card",
  uploadAccept,
  busyId,
  disabled,
}: {
  items: LibItem[];
  activeId: string | null;
  onSelect: (item: LibItem) => void | Promise<unknown>;
  onUpload?: () => void | Promise<unknown>;
  onReset?: () => void | Promise<unknown>;
  view?: "card" | "list";
  uploadAccept?: string;
  /** Item id (or one of the sentinel keys) the host knows to be loading. */
  busyId?: string | null;
  /** Lock every card — e.g. while the host runs a load started elsewhere. */
  disabled?: boolean;
}) {
  const [pending, setPending] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  // LeftPanel unmounts the panel body when the panel is closed, which can
  // happen mid-load; without this guard the settle handler would setState on an
  // unmounted grid.
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  const busyKey = busyId ?? pending;
  const locked = disabled === true || busyKey !== null;

  const run = (key: string, fn: () => void | Promise<unknown>) => {
    if (locked) return;
    setFailed(null);
    let r: void | Promise<unknown>;
    try {
      r = fn();
    } catch (e) {
      setFailed(errText(e));
      return;
    }
    if (!r || typeof (r as Promise<unknown>).then !== "function") return;
    setPending(key);
    (r as Promise<unknown>).then(
      () => { if (alive.current) setPending(null); },
      (e) => { if (!alive.current) return; setPending(null); setFailed(errText(e)); },
    );
  };

  const error = failed ? <span className="text-[11px] text-ed-red">{failed}</span> : null;
  /** Dim + block a card that isn't the one currently loading. */
  const lockCls = (self: boolean) => (locked && !self ? " pointer-events-none opacity-50" : "");

  if (view === "list") {
    return (
      <div className="flex flex-col gap-1">
        {onReset ? (
          <button onClick={() => run(RESET_KEY, onReset)} disabled={locked} aria-busy={busyKey === RESET_KEY || undefined}
            className={`flex items-center gap-2 rounded-lg border border-ed-border bg-white px-2.5 py-2 text-left text-[13px] text-ed-text hover:border-ed-accent${lockCls(busyKey === RESET_KEY)}`}>
            {busyKey === RESET_KEY
              ? <Icon name="progress_activity" size={15} className="animate-spin text-ed-text-3" />
              : <Icon name="block" size={15} className="text-ed-text-3" />} None
          </button>
        ) : null}
        {items.map((it) => {
          const on = activeId === it.id;
          const loading = busyKey === it.id;
          return (
            <button key={it.id} onClick={() => run(it.id, () => onSelect(it))} disabled={locked} aria-busy={loading || undefined}
              className={`flex items-center justify-between gap-2 rounded-lg border px-2.5 py-2 text-left text-[13px] transition-colors ${on ? "border-ed-accent bg-ed-active text-ed-text" : "border-ed-border bg-white text-ed-text hover:border-ed-accent"}${lockCls(loading)}`}>
              <span className="truncate capitalize">{it.label}</span>
              {loading ? <Icon name="progress_activity" size={15} className="animate-spin" /> : on ? <Icon name="check" size={15} /> : null}
            </button>
          );
        })}
        {onUpload ? (
          <button onClick={() => run(UPLOAD_KEY, onUpload)} disabled={locked} aria-busy={busyKey === UPLOAD_KEY || undefined}
            className={`flex items-center gap-2 rounded-lg border border-dashed border-ed-border px-2.5 py-2 text-left text-[13px] text-ed-text-2 hover:border-ed-accent${lockCls(busyKey === UPLOAD_KEY)}`} title={uploadAccept}>
            {busyKey === UPLOAD_KEY
              ? <Icon name="progress_activity" size={15} className="animate-spin" />
              : <Icon name="add" size={15} />} Add custom
          </button>
        ) : null}
        {error}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      <div className="grid grid-cols-3 gap-2">
        {onReset ? (
          <button onClick={() => run(RESET_KEY, onReset)} disabled={locked} aria-busy={busyKey === RESET_KEY || undefined}
            className={`flex aspect-square flex-col items-center justify-center gap-1 rounded-lg border border-ed-border bg-white text-ed-text-3 hover:border-ed-accent${lockCls(busyKey === RESET_KEY)}`}>
            <Icon name={busyKey === RESET_KEY ? "progress_activity" : "block"} size={18} className={busyKey === RESET_KEY ? "animate-spin" : undefined} />
            <span className="text-[11px]">None</span>
          </button>
        ) : null}
        {items.map((it) => {
          const on = activeId === it.id;
          const loading = busyKey === it.id;
          return (
            <button key={it.id} onClick={() => run(it.id, () => onSelect(it))} disabled={locked} aria-busy={loading || undefined} title={it.label}
              className={`group relative aspect-square overflow-hidden rounded-lg border transition-colors ${on ? "border-ed-accent ring-2 ring-ed-accent/40" : "border-ed-border hover:border-ed-accent"}${lockCls(loading)}`}>
              <div className="absolute inset-0 bg-ed-border" style={it.swatch ? { background: it.swatch } : it.thumb ? { backgroundImage: `url(${it.thumb})`, backgroundSize: "cover", backgroundPosition: "center" } : undefined} />
              <span className="absolute inset-x-0 bottom-0 truncate bg-black/45 px-1 py-0.5 text-center text-[11px] capitalize text-white opacity-0 transition-opacity group-hover:opacity-100">{it.label}</span>
              {loading ? (
                <span className="absolute inset-0 flex items-center justify-center bg-black/40 text-white">
                  <Icon name="progress_activity" size={18} className="animate-spin" />
                </span>
              ) : on ? (
                <span className="absolute right-1 top-1 rounded-full bg-ed-accent p-0.5 text-white"><Icon name="check" size={11} /></span>
              ) : null}
            </button>
          );
        })}
        {onUpload ? (
          <button onClick={() => run(UPLOAD_KEY, onUpload)} disabled={locked} aria-busy={busyKey === UPLOAD_KEY || undefined}
            className={`flex aspect-square flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-ed-border text-ed-text-2 hover:border-ed-accent${lockCls(busyKey === UPLOAD_KEY)}`} title={uploadAccept}>
            <Icon name={busyKey === UPLOAD_KEY ? "progress_activity" : "add"} size={18} className={busyKey === UPLOAD_KEY ? "animate-spin" : undefined} />
            <span className="text-[11px]">Add</span>
          </button>
        ) : null}
      </div>
      {error}
    </div>
  );
}
